//
import { RegionHelper } from "@/common/region_helper";
import axios, { AxiosResponse } from "axios";
import { Coordinate } from "./EventsMap.vue";

/**
 * DB model CPD event as reveived from the server.
 */
export type DbCpdEvent = {
  id: string;
  capacity: number;
  capacity_is_custom: boolean;
  category_id: string;
  changed: string;
  created: string;
  currency: string;
  description: {
    html: string;
    text: string;
  };
  start: {
    local: string;
    timezone: string;
    utc: string;
  };
  end: {
    local: string;
    timezone: string;
    utc: string;
  };
  name: {
    html: string;
    text: string;
  };
  online_event: boolean;
  status: string;
  url: string;
  venue?: {
    name?: string;
    address: {
      address_1: string;
      address_2: string;
      city: string;
      country: string;
      latitude: string;
      localized_address_display: string;
      localized_area_display: string;
      localized_multi_line_address_display: string[];
      longitude: string;
      postal_code: string;
      region: string;
    };
    id: string;
    latitude: string;
    longitude: string;
  };
};

type DbLiveCpdEvent = Omit<DbCpdEvent, "status"> &
  Required<Pick<DbCpdEvent, "venue">> & { status: "live" };

/**
 * App model CPD event.
 */
export type CPDEvent = {
  id: string;
  url: string;
  title: string;
  online: boolean;
  status: string;
  startTime: Date;
  address?: string;
  coord?: Coordinate;
  distance?: number;
};

/**
 * Fetch the CPD events from the server.
 */
export async function fetchEvents(): Promise<CPDEvent[]> {
  const region = RegionHelper.getRegionFromLink(location.pathname);
  const response: AxiosResponse<DbCpdEvent[]> = await axios.get(
    `/cpd-events/region/${region.country_code}/`
  );

  return response.data.map((event) => ({
    id: event.id,
    url: event.url,
    title: event.name.text,
    online: event.online_event,
    status: event.status,
    startTime: new Date(event.start.utc),
    address: getEventDisplayAddress(event),
    coord: getEventCoordinates(event),
  }));
}

function isDbLiveEvent(event: DbCpdEvent): event is DbLiveCpdEvent {
  return !event.online_event && !!event.venue;
}

function getEventDisplayAddress(event: DbCpdEvent): string | undefined {
  return isDbLiveEvent(event)
    ? event.venue.name + ", " + event.venue.address.localized_address_display
    : undefined;
}

function getEventCoordinates(event: DbCpdEvent) {
  return isDbLiveEvent(event)
    ? {
        lat: parseFloat(event.venue.latitude),
        lng: parseFloat(event.venue.longitude),
      }
    : undefined;
}

/**
 * Fetch the coordinates for a given postcode.
 */
export function getPostcodeCoordinates(
  postcode: string
): Promise<Coordinate | null> {
  if (postcode.length < 3) {
    return Promise.resolve(null);
  }

  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      { address: postcode + " United Kingdom" },
      (results, status) => {
        if (status === "OK" && results) {
          resolve({
            lat: results[0].geometry.location.lng(),
            lng: results[0].geometry.location.lat(),
          });
        } else {
          reject(status);
        }
      }
    );
  });
}

/**
 * Get the distance in KM between two coordinates.
 */
export function getDistanceMiles(coord1: Coordinate, coord2: Coordinate) {
  const deg2rad = (deg: number) => deg * (Math.PI / 180);
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(coord2.lat - coord1.lat);
  const dLng = deg2rad(coord2.lng - coord1.lng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(coord1.lat)) *
      Math.cos(deg2rad(coord2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in KM
  return d * 0.621371; // Distance in miles
}
